import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import { useDemo } from '../../contexts/DemoContext';
import { useTheme } from '../../contexts/ThemeContext';

const OverlayContainer = styled.div`
  position: fixed;
  bottom: 8px;
  right: ${props => props.$sidebarExpanded ? 'calc(25% + 8px)' : '16px'};
  z-index: 100005;
  pointer-events: ${props => props.$hasActiveModal ? 'none' : 'auto'};
  opacity: ${props => props.$hasActiveModal ? 0.5 : 1};
  transition: all 0.3s ease;
  isolation: isolate;
`;

const ControlBox = styled.div`
  background: ${({ $isDarkMode }) => $isDarkMode
    ? 'rgba(17, 24, 39, 0.95)'
    : 'rgba(255, 255, 255, 0.98)'};
  color: ${({ $isDarkMode }) => $isDarkMode ? 'white' : '#1e293b'};
  border: 1px solid ${({ $isDarkMode }) => $isDarkMode
    ? 'rgba(255,255,255,0.1)' : '#cbd5e1'};
  backdrop-filter: blur(8px);
  border-radius: 8px;
  padding: ${({ $compact }) => $compact ? '4px 6px' : '8px 10px'};
  width: ${({ $compact }) => $compact ? '120px' : '150px'};
  font-size: ${({ $compact }) => $compact ? '0.75rem' : '0.8rem'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px -1px rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08);
  transition: all 0.2s;
`;

const StatusText = styled.div`
  color: ${({ $isDarkMode }) => $isDarkMode ? 'white' : '#1e293b'};
  font-size: ${({ $compact }) => $compact ? '0.7rem' : '0.8rem'};
  font-weight: 500;
  text-align: center;
  opacity: 0.9;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const Button = styled.button`
  flex: 1;
  padding: ${({ $compact }) => $compact ? '2px 6px' : '4px 10px'};
  border-radius: 6px;
  font-weight: 500;
  font-size: ${({ $compact }) => $compact ? '0.7rem' : '0.8rem'};
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;

  &.pause {
    background: ${props => props.$isPaused ? '#22c55e' : '#eab308'};
    color: white;
    &:hover {
      background: ${props => props.$isPaused ? '#16a34a' : '#ca8a04'};
    }
  }

  &.stop {
    background: #ef4444;
    color: white;
    &:hover {
      background: #dc2626;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: ${props => props.$isPaused ? '#22c55e' : '#eab308'};
    }
  }
`;

const DemoOverlay = ({ 
  isRecording,
  isPaused,
  onPauseToggle,
  onStop,
  hasActiveModal = false,
  sidebarExpanded = false,
  spotlightActive = false // New prop to detect if spotlight is active
}) => {
  const navigate = useNavigate();
  const { stopDemo, isConfirmingStop, setIsConfirmingStop } = useDemo();
  const { isDarkMode } = useTheme();

  // Use spotlightActive to determine if we should show compact mode
  const compact = spotlightActive;

  const handleStop = () => {
    if (!isPaused) {
      onPauseToggle();
    }
    setIsConfirmingStop(true);
  };

  const confirmStop = () => {
    setIsConfirmingStop(false);
    stopDemo();
    navigate('/', { replace: true });
  };

  return createPortal(
    <>
      <OverlayContainer
        data-allow-demo-click="true"
        $hasActiveModal={hasActiveModal}
        $sidebarExpanded={sidebarExpanded}
        onClick={e => e.stopPropagation()}
      >
        {isRecording && (
          <ControlBox $isDarkMode={isDarkMode} $compact={compact}>
            <StatusText $isDarkMode={isDarkMode} $compact={compact}>
              Demo {isPaused ? 'Paused' : 'in Progress'}...
            </StatusText>
            <ButtonGroup>
              <Button 
                className="pause"
                $isPaused={isPaused}
                $compact={compact}
                onClick={onPauseToggle}
                disabled={hasActiveModal}
              >
                {isPaused ? 'Resume' : 'Pause'}
              </Button>
              <Button 
                className="stop"
                $compact={compact}
                onClick={handleStop}
                disabled={hasActiveModal}
              >
                Stop
              </Button>
            </ButtonGroup>
          </ControlBox>
        )}
      </OverlayContainer>

      <Modal
        isOpen={isConfirmingStop}
        onClose={() => setIsConfirmingStop(false)}
        size="x-small"
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Stop Demo?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Are you sure you want to stop the demo? Your progress will not be saved.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsConfirmingStop(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmStop}
              className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
            >
              Stop Demo
            </button>
          </div>
        </div>
      </Modal>
    </>,
    document.body
  );
};

export default DemoOverlay; 